/**
 * Admin · Domain trust bulk editor
 *
 * Trust tiers, allowlist priority and blocked patterns, edited in bulk from a
 * CSV, with the expected trust impact recomputed from stored provenance BEFORE
 * anything is saved. No research is spent to preview.
 */
import { useMemo, useRef, useState } from "react";
import { AlertTriangle, Ban, Check, Download, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { downloadCsv } from "@/lib/csv";
import {
  deleteDomainTrust, mergeTrustRules, parseTrustCsv, previewTrustImpact, saveDomainTrust,
  TRUST_CSV_HEADER, type DomainTrustRule, type EnrichmentRow, type TrustImpact,
} from "@/lib/evidenceEnrichment";

const pct = (v: number) => `${Math.round(v * 100)}%`;

export default function DomainTrustBulkEditor({
  rules,
  rows,
  onSaved,
}: {
  rules: DomainTrustRule[];
  rows: EnrichmentRow[];
  onSaved: () => void | Promise<void>;
}) {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [csv, setCsv] = useState("");
  const [parsed, setParsed] = useState<{ rules: DomainTrustRule[]; errors: string[] } | null>(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Record<string, Partial<DomainTrustRule>>>({});

  const proposed = useMemo(
    () => (parsed?.rules.length ? mergeTrustRules(rules, parsed.rules) : rules),
    [rules, parsed],
  );

  const impact: TrustImpact | null = useMemo(
    () => (parsed?.rules.length ? previewTrustImpact(rows, rules, proposed) : null),
    [parsed, rows, rules, proposed],
  );

  const readFile = async (file: File) => {
    const text = await file.text();
    setCsv(text);
    setParsed(parseTrustCsv(text));
  };

  const apply = async () => {
    if (!parsed?.rules.length) return;
    setSaving(true);
    try {
      await saveDomainTrust(parsed.rules);
      toast({
        title: "Domain trust updated",
        description: `${parsed.rules.length} rule${parsed.rules.length > 1 ? "s" : ""} saved. New runs use them immediately.`,
      });
      setCsv("");
      setParsed(null);
      await onSaved();
    } catch (e) {
      toast({
        title: "Could not save the trust rules",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const saveOne = async (rule: DomainTrustRule) => {
    const patch = draft[rule.pattern] ?? {};
    try {
      await saveDomainTrust([{ ...rule, ...patch }]);
      setDraft((d) => { const n = { ...d }; delete n[rule.pattern]; return n; });
      toast({ title: "Saved", description: rule.pattern });
      await onSaved();
    } catch (e) {
      toast({
        title: "Could not save",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const removeOne = async (pattern: string) => {
    try {
      await deleteDomainTrust(pattern);
      toast({ title: "Removed", description: pattern });
      await onSaved();
    } catch (e) {
      toast({
        title: "Could not remove",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const exportCurrent = () => {
    downloadCsv("domain-trust.csv", [
      TRUST_CSV_HEADER.split(","),
      ...rules.map((r) => [
        r.pattern, r.tier, r.weight, String(r.is_regex), r.priority, String(r.blocked), r.notes ?? "",
      ]),
    ]);
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[14rem]">
            <h3 className="font-medium">Bulk edit</h3>
            <p className="text-sm text-muted-foreground">
              Upload a CSV of trust rules. Priority runs low-to-high, so an allowlist row at
              priority 1 overrides the unknown-domain default of 0.50 for a high-quality source.
              Rows marked blocked are discarded before synthesis.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={exportCurrent}>
            <Download className="h-4 w-4 mr-1.5" aria-hidden="true" /> Export current
          </Button>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
            <Upload className="h-4 w-4 mr-1.5" aria-hidden="true" /> Upload CSV
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            className="sr-only"
            aria-label="Upload a domain trust CSV"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void readFile(f);
              e.target.value = "";
            }}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="trust-csv">CSV</Label>
          <Textarea
            id="trust-csv"
            value={csv}
            rows={6}
            className="font-mono text-xs"
            placeholder={`${TRUST_CSV_HEADER}\nnhs.uk,government,1.00,false,1,false,UK health authority\nsomefarm.com,blocked,0,false,1,true,mirror farm`}
            onChange={(e) => {
              setCsv(e.target.value);
              setParsed(e.target.value.trim() ? parseTrustCsv(e.target.value) : null);
            }}
          />
          <p className="text-xs text-muted-foreground">
            Header required: <span className="font-mono">{TRUST_CSV_HEADER}</span>. Only{" "}
            <span className="font-mono">pattern</span> is mandatory.
          </p>
        </div>

        {parsed && parsed.errors.length > 0 && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs space-y-1">
            <p className="font-medium text-amber-400 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              {parsed.errors.length} row{parsed.errors.length > 1 ? "s" : ""} rejected
            </p>
            <ul className="list-disc pl-5 text-muted-foreground">
              {parsed.errors.slice(0, 12).map((e) => <li key={e}>{e}</li>)}
            </ul>
          </div>
        )}

        {parsed && parsed.rules.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{parsed.rules.length} rules ready</p>
            <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
              {parsed.rules.map((r) => (
                <li key={r.pattern} className="flex flex-wrap items-center gap-2">
                  <span className="font-mono flex-1 min-w-[12rem]">{r.pattern}</span>
                  <Badge variant="outline">{r.tier}</Badge>
                  <span className="tabular-nums">{r.weight.toFixed(2)}</span>
                  <span className="text-muted-foreground">priority {r.priority}</span>
                  {r.is_regex && <Badge variant="outline">regex</Badge>}
                  {r.blocked && (
                    <Badge variant="outline" className="bg-rose-500/15 text-rose-400 border-rose-500/30">
                      blocked
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {impact && (
        <Card className="p-4 space-y-3">
          <h3 className="font-medium">Expected trust impact</h3>
          <p className="text-sm text-muted-foreground">
            Recomputed from the provenance already stored for {impact.rows.length} enrichment
            {impact.rows.length === 1 ? "" : "s"}. Nothing is saved or re-researched until you apply.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              ["Avg trust now", pct(impact.avgBefore)],
              ["Avg trust after", pct(impact.avgAfter)],
              ["Would clear 0.60", `+${impact.gained}`],
              ["Would drop below", `−${impact.lost}`],
              ["Sources blocked", impact.blockedSources],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded-lg border border-border/60 p-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
                <p className="text-xl font-semibold mt-1">{String(value)}</p>
              </div>
            ))}
          </div>
          <table className="w-full text-sm">
            <caption className="sr-only">Per-role trust change under the proposed rules</caption>
            <thead className="text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th scope="col" className="text-left py-1">Role</th>
                <th scope="col" className="text-right py-1">Now</th>
                <th scope="col" className="text-right py-1">After</th>
                <th scope="col" className="text-right py-1">Change</th>
                <th scope="col" className="text-left py-1 pl-3">Effect</th>
              </tr>
            </thead>
            <tbody>
              {impact.rows.slice(0, 40).map((r) => (
                <tr key={r.entity_id} className="border-t border-border/50">
                  <td className="py-1">
                    {r.entity_name}
                    {r.blockedSources.length > 0 && (
                      <span className="block text-xs text-muted-foreground">
                        blocked: {r.blockedSources.join(", ")}
                      </span>
                    )}
                  </td>
                  <td className="py-1 text-right tabular-nums">{pct(r.before)}</td>
                  <td className="py-1 text-right tabular-nums">{pct(r.after)}</td>
                  <td
                    className={`py-1 text-right tabular-nums ${
                      r.delta > 0 ? "text-emerald-400" : r.delta < 0 ? "text-rose-400" : ""
                    }`}
                  >
                    {r.delta > 0 ? "+" : ""}{pct(r.delta)}
                  </td>
                  <td className="py-1 pl-3 text-xs">
                    {!r.promotableBefore && r.promotableAfter
                      ? "becomes promotable"
                      : r.promotableBefore && !r.promotableAfter
                      ? "loses promotability"
                      : "no change to gating"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {impact.rows.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No stored provenance matches these patterns yet, so nothing changes today.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void apply()} disabled={saving}>
              <Check className="h-4 w-4 mr-1.5" aria-hidden="true" /> Apply {parsed?.rules.length} rules
            </Button>
            <Button variant="outline" onClick={() => { setParsed(null); setCsv(""); }}>
              Discard
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-4 space-y-2">
        <h3 className="font-medium">Live rules ({rules.length})</h3>
        <p className="text-sm text-muted-foreground">
          First match in priority order wins. Unknown domains score 0.50 unless an allowlist rule
          covers them.
        </p>
        <ul className="space-y-2 text-sm">
          {rules.map((r) => {
            const d = { ...r, ...(draft[r.pattern] ?? {}) };
            const dirty = !!draft[r.pattern];
            return (
              <li key={r.pattern} className="flex flex-wrap items-center gap-2 border-t border-border/40 pt-2">
                <span className="flex-1 min-w-[12rem] font-mono text-xs">{r.pattern}</span>
                <Badge variant="outline">{d.tier}</Badge>
                <div className="flex items-center gap-1">
                  <Label htmlFor={`w-${r.pattern}`} className="text-xs text-muted-foreground">weight</Label>
                  <Input
                    id={`w-${r.pattern}`}
                    className="h-9 w-20"
                    type="number" min={0} max={1} step={0.05}
                    value={d.weight}
                    onChange={(e) =>
                      setDraft((s) => ({ ...s, [r.pattern]: { ...s[r.pattern], weight: Number(e.target.value) } }))
                    }
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Label htmlFor={`p-${r.pattern}`} className="text-xs text-muted-foreground">priority</Label>
                  <Input
                    id={`p-${r.pattern}`}
                    className="h-9 w-20"
                    type="number" min={1} step={1}
                    value={d.priority}
                    onChange={(e) =>
                      setDraft((s) => ({ ...s, [r.pattern]: { ...s[r.pattern], priority: Number(e.target.value) } }))
                    }
                  />
                </div>
                <Button
                  variant={d.blocked ? "destructive" : "outline"}
                  size="sm"
                  onClick={() =>
                    setDraft((s) => ({ ...s, [r.pattern]: { ...s[r.pattern], blocked: !d.blocked } }))
                  }
                  aria-pressed={d.blocked}
                >
                  <Ban className="h-4 w-4 mr-1.5" aria-hidden="true" /> {d.blocked ? "Blocked" : "Allowed"}
                </Button>
                <Button size="sm" disabled={!dirty} onClick={() => void saveOne(r)}>Save</Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => void removeOne(r.pattern)}
                  aria-label={`Remove ${r.pattern}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
                {r.notes && <span className="text-xs text-muted-foreground w-full">{r.notes}</span>}
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
